import React, { type JSX } from "react";

import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { useFormAtom } from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { FormField, FormLabel } from "@impero-com/ui";
import { Typography } from "antd";
import { useAtom } from "jotai";

import { FileUploader } from "components/fileUploader/fileUploader";
import { DeleteBlobAction } from "components/fileUploader/queries/useDeleteBlobMutation";
import { UploaderFile } from "components/fileUploader/types";

import * as theme from "../fields/theme.css";

// ! Unlike RHFFileUploader, this input has `UploaderFile[]` as its value type.
// ! We would like to move away from this, but we don't anticipate additional usage of this component.

const { Text } = Typography;
interface FAFileUploaderInputProps {
    atom: FormAtom<UploaderFile[]>;
    preUploadCallback?: () => Promise<any>;
    disabled?: boolean;
    emptyText?: string;
    label?: React.ReactNode;
    deleteAction: DeleteBlobAction;
    hideMissing?: boolean;
    /**
     * Maximum number of files that can be uploaded. If not specified, there is no limit.
     */
    maxFiles?: number;
}
export const FAFileUploaderInput = ({
    atom,
    preUploadCallback,
    emptyText,
    label,
    deleteAction,
    disabled = false,
    hideMissing = false,
    maxFiles = undefined,
}: FAFileUploaderInputProps): JSX.Element => {
    const [value, setValue] = useAtom(atom);
    const { error } = useFormAtom(atom);
    const hasFileErrors = value.some((file) => file.status === "error");
    const hasSchemaErrors = error
        ? !value.some((file) => file.status === "uploading")
        : false;

    return (
        <FormField>
            {!!label && <FormLabel disabled={disabled}>{label}</FormLabel>}

            <div className={theme.fileUploaderInputLayout}>
                <FileUploader
                    value={value}
                    setValue={(value) => void setValue(value)}
                    preUploadCallback={preUploadCallback}
                    disabled={disabled}
                    emptyText={emptyText}
                    deleteAction={deleteAction}
                    hideMissing={hideMissing}
                    maxFiles={maxFiles}
                />
                {(hasFileErrors || hasSchemaErrors) && (
                    <Text className={ERRORED_CLASSNAME} type="danger">
                        {error?.value}
                    </Text>
                )}
            </div>
        </FormField>
    );
};
